import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { dynamo, auth } from "src/utils";
import { userTypes } from "src/utils/types";
import {
  Owner,
  Manager,
  Consultant,
  Client,
  ReferralPartner,
  Subscriber,
} from "src/utils/types/UserTypes";

export type AuthUser =
  | Owner
  | Manager
  | Consultant
  | Client
  | ReferralPartner
  | Subscriber;

type AuthContextType = {
  user: AuthUser | null;
  tokens: Record<any, any> | null;
  setUser: (user: AuthUser) => void;
  setTokens: (tokens: Record<any, any>) => void;
  refetchUser: (userId: string) => void;
  logout: () => void;
  checkBillingType: (userType: userTypes.AccountTypes) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tokens, setTokens] = useState<Record<any, any> | null>(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const checkBillingType = (userType: userTypes.AccountTypes) => {
    return userType !== userTypes.AccountTypes.MANAGER &&
      userType !== userTypes.AccountTypes.NEATT &&
      userType !== userTypes.AccountTypes.OWNER
      ? true
      : false;
  };

  const logout = () => {
    setUser(null);
    setTokens(null);

    localStorage.removeItem("user");
    localStorage.removeItem("tokens");

    navigate("/");
  };

  const refetchUser = async (userId: string) => {
    const { user } = await dynamo.fetch.userById(userId);

    setUser(user);
    localStorage.setItem("user", JSON.stringify(user));
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedTokens = localStorage.getItem("tokens");

    if (storedUser) setUser(JSON.parse(storedUser));
    if (storedTokens) setTokens(JSON.parse(storedTokens));
  }, []);

  useEffect(() => {
    const isPublic = auth.publicRoutes.some((route) =>
      pathname.startsWith(route)
    );

    if (!user && !isPublic) navigate("/auth/login");
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        setTokens,
        setUser,
        refetchUser,
        checkBillingType,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
